/**
 * useAutosave — Etappe 3 (Jun 2026, User-Wunsch: Global Autosave)
 *
 * Speichert ein Formular automatisch, sobald sich sein Wert ändert (debounced).
 * Ersetzt manuelle „Speichern"-Buttons. Funktioniert mit dem vorhandenen
 * `SaveStatusIndicator` (Props: saving, dirty, savedAt).
 *
 * Verhalten:
 *  - Beim ersten Render (Initialwert / geladene Daten) wird NICHT gespeichert.
 *  - Jede spätere Wertänderung markiert `dirty=true` und plant nach `delay` ms
 *    einen Save. Schnelle Eingaben werden zusammengefasst (debounce).
 *  - Toggles/Selects wirken durch den kurzen Delay praktisch sofort.
 *
 * Wichtig: Die `saveFn` sollte KEIN globales Refresh auslösen, das den
 * Formular-State zurücksetzt (sonst droht eine Save-Schleife). Nur PATCHen.
 *
 * @param {*} value         Der zu überwachende Formularwert (Objekt/Primitive).
 * @param {(v:any)=>Promise} saveFn  Persistiert den aktuellen Wert.
 * @param {object} opts      { delay=700, enabled=true, deps }
 * @returns {{saving:boolean, dirty:boolean, savedAt:number|null, saveNow:()=>void}}
 */
import { useEffect, useRef, useState, useCallback } from "react";

export function useAutosave(value, saveFn, { delay = 700, enabled = true, deps } = {}) {
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const first = useRef(true);
    const timer = useRef(null);
    const latest = useRef(value);
    latest.current = value;
    const saveRef = useRef(saveFn);
    saveRef.current = saveFn;

    const runSave = useCallback(async () => {
        try {
            setSaving(true);
            setDirty(false);
            await saveRef.current(latest.current);
            setSavedAt(Date.now());
        } catch (e) {
            setDirty(true);
        } finally {
            setSaving(false);
        }
    }, []);

    const key = deps !== undefined ? deps : JSON.stringify(value);

    useEffect(() => {
        if (!enabled) return undefined;
        if (first.current) {
            first.current = false;
            return undefined;
        }
        setDirty(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(runSave, delay);
        return () => { if (timer.current) clearTimeout(timer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, enabled]);

    const saveNow = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        runSave();
    }, [runSave]);

    return { saving, dirty, savedAt, saveNow };
}

export default useAutosave;
